import {
  Conversation,
  ConversationMessage,
  type ConversationContext,
} from '@stripe-internal/conversations-client';
import {Subscription} from 'rxjs';
import {ANIMATION_DURATION} from '../animation';

const POLL_INTERVAL_MS = 1000;
const MESSAGE_REVEAL_DELAY_MS = ANIMATION_DURATION + 100;

type PollInputModes = {
  enabledInputModes: string[];
  messageId: string;
};

export type PollFn = () => Promise<
  | {
      type: 'success';
      messages: Record<string, ConversationMessage>;
      inputModes?: PollInputModes;
      context?: ConversationContext;
      // todo(@danielxu): temporary — remove once the experiment is over.
      temporaryExperimentVariant?: string;
    }
  | {type: 'error'; error: string; code: number}
>;

export interface ConversationPollerState {
  isComplete: boolean;
  error: string | null;
}

export class ConversationPoller {
  private _poll: PollFn;

  private _conversation: Conversation;

  private _state: ConversationPollerState;

  private _listeners = new Set<() => void>();

  private _polling = false;

  private _timeout: ReturnType<typeof setTimeout> | undefined;

  private _abortController: AbortController | undefined;

  private _eventSubscription: Subscription | undefined;

  constructor(poll: PollFn, conversation: Conversation) {
    this._poll = poll;
    this._conversation = conversation;

    this._state = {
      isComplete: !this._hasDynamicContent(),
      error: null,
    };

    const {log} = this._conversation;
    if (log) {
      this._eventSubscription = log.observable().subscribe(() => {
        if (this._state.isComplete && this._hasDynamicContent()) {
          this._state = {isComplete: false, error: null};
          this._doPoll();
        }
      });
    }
  }

  private _hasDynamicContent(): boolean {
    if (!this._conversation.messages) {
      return false;
    }
    const messages = Object.values(this._conversation.messages);
    return messages.some((message) => {
      if (
        message.type === 'custom-part-conversation-message' &&
        'part' in message
      ) {
        const {dynamic} = (message as any).part;
        if (dynamic === 'if_latest' || dynamic === 'active') {
          return true;
        }
      }
      const dataDynamic =
        message.data &&
        typeof message.data === 'object' &&
        'dynamic' in message.data
          ? message.data.dynamic
          : undefined;
      return dataDynamic === 'if_latest' || dataDynamic === 'active';
    });
  }

  getState(): ConversationPollerState {
    return this._state;
  }

  subscribe(callback: () => void): () => void {
    this._listeners.add(callback);
    return () => this._listeners.delete(callback);
  }

  startPolling() {
    if (this._polling || this._state.isComplete) {
      return;
    }
    this._doPoll();
  }

  stopPolling() {
    this._abortController?.abort();
    this._polling = false;
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
    }
  }

  destroy() {
    this.stopPolling();
    this._eventSubscription?.unsubscribe();
    this._listeners.clear();
  }

  private setState(next: Partial<ConversationPollerState>) {
    this._state = {...this._state, ...next};
    this._listeners.forEach((listener) => listener());
  }

  private async waitForMessageReveal(signal: AbortSignal): Promise<void> {
    await new Promise<void>((resolve) => {
      let timeout: ReturnType<typeof setTimeout> | undefined;
      const resolveOnce = () => {
        if (timeout) {
          clearTimeout(timeout);
        }
        signal.removeEventListener('abort', resolveOnce);
        resolve();
      };

      timeout = setTimeout(resolveOnce, MESSAGE_REVEAL_DELAY_MS);
      signal.addEventListener('abort', resolveOnce, {once: true});
    });
  }

  private async applyMessages(
    messages: Record<string, ConversationMessage>,
    signal: AbortSignal,
  ) {
    const sorted = Object.values(messages).sort(
      (a, b) => a.timestamp - b.timestamp,
    );

    await sorted.reduce<Promise<void>>(async (previous, message, index) => {
      await previous;
      if (signal.aborted) {
        return;
      }

      if (index > 0) {
        await this.waitForMessageReveal(signal);
      }

      if (signal.aborted) {
        return;
      }

      await this._conversation.handleEventFromClient({
        id: `${message.id}-${Date.now()}`,
        timestamp: Date.now(),
        name: 'conversation-message-updated',
        conversationId: this._conversation.id,
        participantId: message.participant.id,
        messageId: message.id,
        nonce: message.nonce,
        message,
      });
    }, Promise.resolve());
  }

  private scheduleNextPoll() {
    this._timeout = setTimeout(() => this._doPoll(), POLL_INTERVAL_MS);
  }

  private serverMessageIdFromMessage(message: ConversationMessage) {
    const data = message.data as
      | {conversationsApi?: {messageId?: string}}
      | undefined;
    return data?.conversationsApi?.messageId;
  }

  // Apply the settled input modes from a poll, but only when the source
  // bot message is still the newest bot message in the conversation.
  private applyPollInputModes(inputModes: PollInputModes | undefined) {
    if (!inputModes) {
      return;
    }

    const {messages} = this._conversation;
    const latestAssistantMessage = messages
      ? [...Object.values(messages)]
          .reverse()
          .find((message) => message.participant.role === 'bot')
      : undefined;

    if (
      latestAssistantMessage &&
      inputModes.messageId !==
        this.serverMessageIdFromMessage(latestAssistantMessage)
    ) {
      return;
    }

    this._conversation.handleEventFromClient({
      id: `poll-input-modes`,
      timestamp: Date.now(),
      name: 'conversation-set-context-values',
      conversationId: this._conversation.id,
      values: {enabledInputModes: inputModes.enabledInputModes},
    });
  }

  private async _doPoll() {
    if (this._polling || this._state.isComplete) {
      return;
    }

    this._polling = true;
    this._abortController = new AbortController();
    const {signal} = this._abortController;

    try {
      const result = await this._poll();

      if (signal.aborted) {
        return;
      }

      if (result.type === 'error') {
        this._polling = false;
        if (result.code === 408) {
          this.scheduleNextPoll();
          return;
        }

        this.setState({isComplete: true, error: result.error});
        return;
      }

      await this.applyMessages(result.messages, signal);

      if (signal.aborted) {
        return;
      }

      this.applyPollInputModes(result.inputModes);

      // todo(@danielxu): temporary — inject the experiment variant into the existing
      // button-options message so ChatInput can read it. Remove once the experiment is over.
      if (result.temporaryExperimentVariant) {
        const optionsMessage = Object.values(this._conversation.messages)
          .filter((m) => m.type === 'button-options-conversation-message')
          .at(-1);
        if (optionsMessage) {
          await this._conversation.handleEventFromClient({
            id: `${optionsMessage.id}-experiment-variant`,
            timestamp: Date.now(),
            name: 'conversation-message-added',
            conversationId: this._conversation.id,
            participantId: optionsMessage.participant.id,
            messageId: optionsMessage.id,
            nonce: optionsMessage.nonce,
            message: {
              ...optionsMessage,
              experimentVariant: result.temporaryExperimentVariant,
            },
          });
        }
      }

      if (signal.aborted) return;
      if (result.context) this._conversation.setContextValues(result.context);
      if (signal.aborted) return;
      this._polling = false;

      if (!this._hasDynamicContent()) {
        this.setState({isComplete: true});
        return;
      }

      this.scheduleNextPoll();
    } catch (e) {
      if (signal.aborted) {
        return;
      }

      this._polling = false;

      this.setState({
        isComplete: true,
        error: e instanceof Error ? e.message : String(e),
      });
    }
  }
}
